import { c } from 'ttag';

import { RoleNotGrantableReason } from '../../../graphql/__generated__/schema';

// grantableByViewer is judged on the actor's own membership: false means "this role, never", not "not on this account".
export interface OnboardingRole {
    id?: string | null;
    name?: string | null;
    grantableByViewer: boolean;
    notGrantableReason?: RoleNotGrantableReason | null;
}

/**
 * Why a role is refused, in the operator's terms, never the API's sentence. Two lengths: a tag
 * beside the greyed option, and a rule said once under the field. Refused roles stay in the list.
 */
export interface RoleRefusal {
    /** Short qualifier, printed on the disabled option. */
    tag: string;
    /** The rule behind it and the gesture out, printed once under the field. */
    rule: string;
}

export const roleRefusal = (reason: RoleNotGrantableReason | null | undefined): RoleRefusal => {
    switch (reason) {
        case RoleNotGrantableReason.PrivilegeEscalation:
            return {
                tag: c('Onboarding role refusal').t`Beyond your own rights`,
                rule: c('Onboarding role refusal')
                    .t`Some roles grant rights you do not carry yourself, and nobody grants more than they hold — so they are offered here without being pickable. The referential is complete: pick a narrower role, or ask an administrator to widen yours.`,
            };
        case RoleNotGrantableReason.MissingPermission:
            return {
                tag: c('Onboarding role refusal').t`User management right missing`,
                rule: c('Onboarding role refusal')
                    .t`You do not carry the right to administer people, so no role can be granted by you at all. The invitation would be refused the same way when sent: ask an administrator for that right.`,
            };
        case RoleNotGrantableReason.NoActiveMembership:
            // Unreachable through roles (NOT_AUTHENTICATED refuses first), but worded rather than
            // folded into the fallback, so the query ever returning it would not be a silent grey row.
            return {
                tag: c('Onboarding role refusal').t`No active membership`,
                rule: c('Onboarding role refusal')
                    .t`Your session no longer carries an active membership, so no role can be granted by you. Sign in again — the draft entered so far lives in this page and will not survive it.`,
            };
        default:
            // grantableByViewer false with no reason: still better than a silently greyed row, and worth reporting.
            return {
                tag: c('Onboarding role refusal').t`Not grantable`,
                rule: c('Onboarding role refusal')
                    .t`Some roles are not grantable by you, and the reason did not come with them. Pick another role, and report it: the screen expects a reason next to every refusal.`,
            };
    }
};

// One rule per distinct reason, deduplicated: the reason is a fact about the operator, not the role.
export const refusalRules = (roles: OnboardingRole[]): string[] => [
    ...new Set(
        roles.filter((role) => !role.grantableByViewer).map((role) => roleRefusal(role.notGrantableReason).rule)
    ),
];

// The role behind an id, or null (including before roles arrive): an unknown id never becomes a refusal.
export const findRole = (roles: OnboardingRole[], roleId: string | null): OnboardingRole | null =>
    (roleId && roles.find((role) => role.id === roleId)) || null;

/** Whether this exact role, once picked, may still be granted. */
export const isRefused = (roles: OnboardingRole[], roleId: string | null): boolean =>
    findRole(roles, roleId)?.grantableByViewer === false;
